import { Autocomplete, Stack, TextField, InputAdornment, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import AppDocuServiceCall from "../_Utils/AppDocuServiceCall";
import { packetStatusOptions, comBrandOptions, ecuTypeOptions, renderComBrandOption, renderEcuTypeOption, renderPacketStatusOption } from "../_Utils/AppDocuCommon";

/**
 * PacketDialogForm - Form comune per AddPacketDialog e EditPacketDialog
 * Props:
 *   itemData: oggetto dati package
 *   onChange: funzione (nuovoItemData) => void
 *   disabled: boolean (opzionale)
 */
export default function PacketDialogForm({ itemData, onChange, disabled, packageCodes = [] }) {
  const [prevName, setPrevName] = useState(null);
  const [autoApplied, setAutoApplied] = useState(false);

  const canAuto = Boolean(itemData && (
    (itemData.kit_code && String(itemData.kit_code).trim()) ||
    (itemData.rail_code && String(itemData.rail_code).trim()) ||
    (itemData.rail2_code && String(itemData.rail2_code).trim()) ||
    ((itemData.other_accessories || '').trim())
  ));

  const generateDescription = () => {
  const message = "Il pulsante AUTO costruisce automaticamente la Description concatenando la descrizione (desc) dei codici trovati nei dati di sistema per Kit Code, Rail Code, Rail2 Code e per ogni codice in Other Accessories (uno per riga). Se per un codice non esiste una descrizione viene usato il codice stesso. Procedere?";
  if (!window.confirm(message)) return;

  const parts = [];
    const pushDescFor = (code) => {
      if (!code) return;
      const c = String(code).trim();
      if (!c) return;
      const found = packageCodes.find(p => String(p.code).trim() === c);
      if (found && found.desc) parts.push(found.desc);
      else parts.push(c);
    };

    // single codes
    pushDescFor(itemData.kit_code);
    pushDescFor(itemData.rail_code);
    pushDescFor(itemData.rail2_code);

    // other_accessories may contain multiple codes, one per line
  const others = (itemData.other_accessories || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    others.forEach(pushDescFor);

    const desc = parts.join(' / ');
    // save previous value so we can undo
    setPrevName(itemData.name || '');
    setAutoApplied(true);
    onChange({ ...itemData, name: desc });
  };

  const undoDescription = () => {
    if (prevName === null) return;
    onChange({ ...itemData, name: prevName });
    setAutoApplied(false);
    setPrevName(null);
  };
    
  const CodeAutocomplete = ({ field, label }) => {
    const [inputVal, setInputVal] = useState(itemData[field] || '');
    const getOptionLabel = (opt) => typeof opt === 'string' ? opt : (opt && opt.code) || '';
    const isOptionEqualToValue = (option, value) => {
      if (!option || value === undefined || value === null) return false;
      const val = typeof value === 'string' ? value : (value.code || '');
      return option.code === val;
    };
    const renderOptionItem = (props, option) => (
      // <li {...props} key={option.code} style={{ padding: 8 }}>
      //   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      //     <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      //       <div style={{ fontWeight: 700 }}>{option.code}</div>
      //       <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', marginTop: 4 }}>{option.desc}</div>
      //     </div>
      //     <div style={{ fontSize: 12, opacity: 0.8, marginLeft: 12 }}>{option.type}</div>
      //   </div>
      // </li>
      <li {...props} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontWeight: 700 }}>{option.code}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', marginTop: 0 }}>{option.desc}</div>
        </span>
        {option.type && option.type && (
          <span style={{ fontSize: 12, color: 'black', marginLeft: 12 }}>{option.type}</span>
        )}
      </li>
    );
    const filterOptionsFn = (opts, { inputValue }) => {
      if (!inputValue) return opts;
      const tokens = inputValue.split(';').map(t => t.trim().toLowerCase()).filter(Boolean);
      if (tokens.length === 0) return opts;
      return opts.filter(opt => {
        const hay = `${opt.code} ${opt.desc || ''} ${opt.type || ''}`.toLowerCase();
        return tokens.every(tok => hay.indexOf(tok) !== -1);
      });
    };

    useEffect(() => {
      // keep local input in sync when parent value changes externally
      setInputVal(itemData[field] || '');
    }, [itemData[field]]);

    return (
      <Autocomplete
        freeSolo
        fullWidth
        options={packageCodes || []}
        groupBy={(option) => (option && option.type) ? String(option.type) : ''}
        renderGroup={(params) => {
          const count = React.Children.count(params.children);
          const hasGroup = params.group && String(params.group).trim().length > 0;
          return (
            <li key={params.key} style={{ listStyle: 'none', padding: 0 }}>
              <div style={{ padding: '10px', fontSize: 12, fontWeight: 700, background: '#e8f0ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {hasGroup ? (
                    <span>{params.group}</span>
                  ) : (
                    <em style={{ fontStyle: 'italic', color: 'rgba(0,0,0,0.7)' }}>no type</em>
                  )}
                </div>
                <div style={{ fontSize: 12, opacity: 0.85 }}>{count} items</div>
              </div>
              <ul style={{ margin: 0, padding: 0 }}>{params.children}</ul>
            </li>
          );
        }}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        value={itemData[field] || ''}
        onChange={(_, newValue) => {
          if (!newValue) {
            setInputVal('');
            return onChange({ ...itemData, [field]: '' });
          }
          if (typeof newValue === 'string') {
            setInputVal(newValue);
            return onChange({ ...itemData, [field]: newValue });
          }
          const code = newValue.code || '';
          setInputVal(code);
          return onChange({ ...itemData, [field]: code });
        }}
        inputValue={inputVal}
        onInputChange={(_, newInput) => setInputVal(newInput)}
        onBlur={() => {
          if ((itemData[field] || '') !== inputVal) {
            onChange({ ...itemData, [field]: inputVal });
          }
        }}
        renderOption={renderOptionItem}
        filterOptions={filterOptionsFn}
        renderInput={(params) => <TextField {...params} label={label + " (usa ; per ricerca AND)"} disabled={disabled} />}
        // disableClearable
        autoHighlight
      />
    );
  };

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        label="Description"
        value={itemData.name || ''}
        onChange={e => onChange({ ...itemData, name: e.target.value })}
        disabled={disabled}
        multiline
        minRows={3}
        maxRows={8}
        onKeyDown={(e) => {
          // Prevent Enter from inserting a newline even if multiline is enabled
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction="column" spacing={0.5} alignItems="flex-end">
                <Button size="small" onClick={generateDescription} disabled={disabled || !canAuto} sx={{ fontSize: 12, minWidth: 56 }}>Auto</Button>
                <Button size="small" onClick={undoDescription} disabled={disabled || !autoApplied} sx={{ fontSize: 12, minWidth: 56 }}>Undo</Button>
              </Stack>
            </InputAdornment>
          )
        }}
      />
      {/* <TextField fullWidth label="Description" value={itemData.description || ''} onChange={e => onChange({ ...itemData, description: e.target.value })} disabled={disabled} /> */}
      <Autocomplete
        freeSolo
        fullWidth
        options={comBrandOptions.map(opt => opt.name)}
        value={itemData.com_brand || ''}
        onChange={(_, newValue) => onChange({ ...itemData, com_brand: newValue || '' })}
        onInputChange={(_, newInputValue) => onChange({ ...itemData, com_brand: newInputValue })}
        renderOption={renderComBrandOption}
        renderInput={(params) => <TextField {...params} label="Com Brand" disabled={disabled} />}
        // disableClearable
        autoHighlight
      />
      <Autocomplete
        freeSolo
        fullWidth
        options={ecuTypeOptions.map(opt => opt.name)}
        value={itemData.ecu_type || ''}
        onChange={(_, newValue) => onChange({ ...itemData, ecu_type: newValue || '' })}
        onInputChange={(_, newInputValue) => onChange({ ...itemData, ecu_type: newInputValue })}
        renderOption={renderEcuTypeOption}
        renderInput={(params) => <TextField {...params} label="ECU Type" disabled={disabled} />}
        // disableClearable
        autoHighlight
      />
      {/* Kit / Rail / Rail2 autocompletes (reuse CodeAutocomplete) */}
      <CodeAutocomplete field="kit_code" label="Kit Code" />
      <CodeAutocomplete field="rail_code" label="Rail Code" />
      <CodeAutocomplete field="rail2_code" label="Rail2 Code (Boxer)" />
      <TextField 
        fullWidth 
        label="Other Accessories" 
        value={itemData.other_accessories || ''} 
        onChange={e => onChange({ ...itemData, other_accessories: e.target.value })}
        multiline 
        minRows={3}
        disabled={disabled}
      />
      <Autocomplete
        freeSolo
        options={packetStatusOptions.map(opt => opt.name)}
        value={itemData.status || ''}
        onInputChange={(_, newValue) => onChange({ ...itemData, status: newValue })}
        renderOption={renderPacketStatusOption}
        renderInput={(params) => (<TextField {...params} fullWidth label="Status" disabled={disabled} />)}
      />
    </Stack>
  );
}
